import type { BoxProps, ButtonProps, HeadingProps, TextProps } from "@chakra-ui/react";

/**
 * Shared design tokens for the aNex glassmorphism design system.
 *
 * These are plain objects that can be spread into Chakra UI component props
 * to ensure visual consistency across all pages.
 *
 * The canonical reference is the LoginSection glassmorphism style.
 */

/** Glassmorphism card surface -- the primary container style. */
export const glassCard: BoxProps = {
  bg: "rgba(13, 13, 21, 0.8)",
  borderRadius: "xl",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "whiteAlpha.100",
  backdropFilter: "blur(12px)",
  p: 6,
};

/** Standard page wrapper -- fills available space with column layout. */
export const pageContainer: BoxProps = {
  w: "100%",
  maxW: "600px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

/** Dashboard page wrapper -- wider to fit heatmap grid. */
export const dashboardContainer: BoxProps = {
  ...pageContainer,
  maxW: "960px",
};

/** Section heading style -- cyan Orbitron headings with letter spacing. */
export const sectionHeading: HeadingProps = {
  color: "cyan.300",
  size: "lg",
  letterSpacing: "0.05em",
  mb: 4,
};

/** Sub-section heading for cards -- smaller cyan heading. */
export const cardHeading: HeadingProps = {
  color: "cyan.300",
  size: "md",
  letterSpacing: "0.05em",
  mb: 3,
  textAlign: "center",
};

/** Primary action button -- transparent with cyan border and hover glow. */
export const actionButton: ButtonProps = {
  bg: "transparent",
  color: "cyan.300",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "cyan.400",
  borderRadius: "md",
  fontWeight: "600",
  letterSpacing: "0.05em",
  _hover: {
    bg: "rgba(0, 255, 255, 0.1)",
    boxShadow: "0 0 20px rgba(0, 255, 255, 0.2)",
  },
  _active: {
    bg: "rgba(0, 255, 255, 0.15)",
  },
};

/** Danger/stop button -- transparent with red border and hover glow. */
export const dangerButton: ButtonProps = {
  bg: "transparent",
  color: "red.400",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "red.400",
  borderRadius: "md",
  fontWeight: "600",
  letterSpacing: "0.05em",
  _hover: {
    bg: "rgba(255, 0, 0, 0.1)",
    boxShadow: "0 0 20px rgba(255, 0, 0, 0.2)",
  },
  _active: {
    bg: "rgba(255, 0, 0, 0.15)",
  },
};

/** Body text for labels and content. */
export const bodyText: TextProps = {
  color: "primary.200",
};

/** Muted / secondary text. */
export const mutedText: TextProps = {
  color: "primary.400",
};

/** Navigation sidebar glass surface. */
export const navSidebar: BoxProps = {
  bg: "rgba(13, 13, 21, 0.85)",
  backdropFilter: "blur(12px)",
  borderRightWidth: "1px",
  borderRightStyle: "solid",
  borderRightColor: "whiteAlpha.100",
  boxShadow: "4px 0 24px rgba(0, 0, 0, 0.3)",
};

/** Active navigation link styling. */
export const navLinkActive = {
  color: "cyan.300",
  fontWeight: "600" as const,
} as const;

/** Inactive navigation link styling. */
export const navLinkInactive = {
  color: "primary.400",
  _hover: {
    color: "primary.200",
    textDecoration: "none",
  },
} as const;
